import { apiClient } from './apiClient';
import type { 
  ApiResponse, 
  WebSocketConnection, 
  WebSocketStats, 
  ProfileConnectionInfo,
  WebSocketMessage 
} from '../types/api';

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private connectPromise: Promise<void> | null = null;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private connectionListeners: ((connected: boolean) => void)[] = [];
  private shouldCloseOnOpen = false;

  /**
   * Get WebSocket connection statistics
   */
  async getConnectionStats(): Promise<ApiResponse<WebSocketStats>> {
    try {
      const response = await apiClient.get<WebSocketStats>('/dev/websocket/stats');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get all active WebSocket connections
   */
  async getActiveConnections(): Promise<ApiResponse<WebSocketConnection[]>> {
    try {
      const response = await apiClient.get<WebSocketConnection[]>('/dev/websocket/connections');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get profile connection information
   */
  async getProfileConnections(): Promise<ApiResponse<ProfileConnectionInfo[]>> {
    try {
      const response = await apiClient.get<ProfileConnectionInfo[]>('/dev/websocket/profiles');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Disconnect a specific WebSocket connection
   */
  async disconnectConnection(connectionId: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.post<boolean>(`/dev/websocket/disconnect/${connectionId}`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Send a message to a specific connection
   */
  async sendMessageToConnection(connectionId: string, message: WebSocketMessage): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.post<boolean>(`/dev/websocket/send/${connectionId}`, message);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Broadcast a message to all connections
   */
  async broadcastMessage(message: WebSocketMessage): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.post<boolean>('/dev/websocket/broadcast', message);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Connect to WebSocket for real-time monitoring
   */
  connect(): Promise<void> {
    // If already connected, return resolved promise
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    // If a connection is in progress, return the existing promise
    if (this.isConnecting && this.connectPromise) {
      return this.connectPromise;
    }

    this.isConnecting = true;
    const wsUrl = this.getWebSocketUrl();
    const token = localStorage.getItem('auth_token');

    if (!token) {
      this.isConnecting = false;
      return Promise.reject(new Error('No authentication token available'));
    }

    this.connectPromise = new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(`${wsUrl}?token=${encodeURIComponent(token)}&type=monitor`);

        this.ws.onopen = () => {
          if (this.shouldCloseOnOpen) {
            // Defer close until after connection established to avoid browser warning
            this.shouldCloseOnOpen = false;
            this.ws?.close(1000, 'Cleanup disconnect');
            return;
          }
          console.log('🔌 WebSocket monitor connected');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.notifyConnectionListeners(true);
          const pending = this.connectPromise;
          this.connectPromise = null;
          resolve();
          // Ensure any callers awaiting the same promise proceed
          void pending;
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('🔌 WebSocket monitor disconnected:', event.code, event.reason);
          const wasConnecting = this.isConnecting;
          this.isConnecting = false;
          this.notifyConnectionListeners(false);

          // If closed during initial connect, reject the pending promise once
          if (this.connectPromise) {
            this.connectPromise = null;
            reject(new Error('WebSocket closed during connection'));
          }

          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts && !wasConnecting) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('🔌 WebSocket monitor error:', error);
          this.isConnecting = false;
          this.connectPromise = null;
          reject(error);
        };

      } catch (error) {
        this.isConnecting = false;
        this.connectPromise = null;
        reject(error as any);
      }
    });

    return this.connectPromise;
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    if (this.ws) {
      // Prevent reconnection attempts after manual disconnect
      this.reconnectAttempts = this.maxReconnectAttempts;

      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(1000, 'Manual disconnect');
        this.ws = null;
      } else if (this.ws.readyState === WebSocket.CONNECTING) {
        // Avoid closing during CONNECTING to prevent browser warning; close right after open
        this.shouldCloseOnOpen = true;
      } else {
        // CLOSED or CLOSING
        this.ws = null;
      }
    } else {
      this.reconnectAttempts = this.maxReconnectAttempts;
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Subscribe to specific message types
   */
  subscribe(messageType: string, handler: (data: any) => void): void {
    this.messageHandlers.set(messageType, handler);
  }

  /**
   * Unsubscribe from message types
   */
  unsubscribe(messageType: string): void {
    this.messageHandlers.delete(messageType);
  }

  /**
   * Add connection status listener
   */
  addConnectionListener(listener: (connected: boolean) => void): void {
    this.connectionListeners.push(listener);
  }

  /**
   * Remove connection status listener
   */
  removeConnectionListener(listener: (connected: boolean) => void): void {
    const index = this.connectionListeners.indexOf(listener);
    if (index > -1) {
      this.connectionListeners.splice(index, 1);
    }
  }

  private getWebSocketUrl(): string {
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:3000';
    return wsUrl.replace(/^http/, 'ws');
  }

  private handleMessage(message: any): void {
    const handler = this.messageHandlers.get(message.type);
    if (handler) {
      handler(message.data);
    }
  }

  private notifyConnectionListeners(connected: boolean): void {
    this.connectionListeners.forEach(listener => listener(connected));
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log(`🔌 Scheduling WebSocket reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    
    setTimeout(() => {
      if (this.reconnectAttempts <= this.maxReconnectAttempts) {
        this.connect().catch(error => {
          console.error('WebSocket reconnect failed:', error);
        });
      }
    }, delay);
  }

  /**
   * Format connection duration
   */
  formatConnectionDuration(connectionTime: string): string {
    const now = new Date();
    const connected = new Date(connectionTime);
    const diffInSeconds = Math.floor((now.getTime() - connected.getTime()) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    return `${Math.floor(diffInSeconds / 86400)}d`;
  }

  /**
   * Format last activity time
   */
  formatLastActivity(lastActivity: string): string {
    const now = new Date();
    const activity = new Date(lastActivity);
    const diffInSeconds = Math.floor((now.getTime() - activity.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  }

  /**
   * Get connection status color for UI
   */
  getConnectionStatusColor(status: string): string {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800';
      case 'disconnected':
        return 'bg-red-100 text-red-800';
      case 'reconnecting':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Get connection status icon
   */
  getConnectionStatusIcon(status: string): string {
    switch (status) {
      case 'connected':
        return '🟢';
      case 'disconnected':
        return '🔴';
      case 'reconnecting':
        return '🟡';
      default:
        return '⚪';
    }
  }
}

// Create and export a singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
